'use client';

import type { AdaptiveScanningStateDto } from '@terra/shared-contracts-applications';
import { HStack } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import { Alert } from '../../components/alert';
import { Button } from '../../components/button';
import { BodyStrong, MetaDefault } from '../../components/typography';

export type AdaptiveScanningPausedAlertProps = {
  pausedReason: AdaptiveScanningStateDto['pausedReason'];
  canResume?: boolean;
  onView: () => void;
  onResume: () => void;
};

export function AdaptiveScanningPausedAlert({
  pausedReason,
  canResume,
  onView,
  onResume,
}: AdaptiveScanningPausedAlertProps) {
  const t = useTranslations('designSystem.adaptiveScanning');
  const tBlocked = useTranslations(
    'designSystem.signals.lifecycle.status.blockedReason',
  );

  return (
    <Alert.Root
      display="flex"
      flexDirection="row"
      bg="bg.neutral.warning"
      borderColor="border.neutral.warning"
      p={2}
      gap={2}
      w="full"
      alignItems="center"
    >
      <Alert.Content flex={1} minW={0}>
        <Alert.Title>
          <BodyStrong color="fg.neutral.default">
            {t('phasePaused')}
            {pausedReason ? ` · ${tBlocked(pausedReason)}` : ''}
          </BodyStrong>
        </Alert.Title>
        <Alert.Description>
          <MetaDefault color="fg.neutral.subtle">
            {t('pausedByAdaptive')}
          </MetaDefault>
        </Alert.Description>
      </Alert.Content>
      <HStack flexShrink={0} alignItems="center" gap={2}>
        <Button size="xs" variant="ghost" onClick={onView}>
          {t('view')}
        </Button>
        {canResume ? (
          <Button size="xs" variant="ghost" onClick={onResume}>
            {t('resume')}
          </Button>
        ) : null}
      </HStack>
    </Alert.Root>
  );
}
